import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { useUserRolesQuery } from '../hooks/useUserRolesQuery';
import { useAvailableRolesQuery } from '../hooks/useAvailableRolesQuery';
import { useAssignRoleMutation } from '../hooks/useAssignRoleMutation';
import { useRemoveRoleMutation } from '../hooks/useRemoveRoleMutation';
import { roleLabel } from '../utils/roleLabel';
import { Alert, Badge, Button, LoadingSpinner, Select } from '@/shared/components';
import { CircleAlertIcon } from 'lucide-react';

interface Props {
  userId: string;
}

export function UserRolesPanel({ userId }: Props) {
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const rolesQuery = useUserRolesQuery(userId);
  const availableRolesQuery = useAvailableRolesQuery();
  const assignMutation = useAssignRoleMutation();
  const removeMutation = useRemoveRoleMutation();

  const currentRoles = rolesQuery.data ?? [];
  const availableRoles = availableRolesQuery.data ?? [];

  const selectedRole = availableRoles.find((r) => r.id === selectedRoleId);
  // Default to elevated when the flag is missing — under-gating an elevated role
  // is a security regression; over-gating a normal role is merely a UX nuisance.
  const isElevated = selectedRole?.is_elevated ?? true;

  // Mutation errors surface via the hook-level handleUserMutationError toast
  // (403 → "Insufficient permissions", 409 → server message like "User already
  // has role 'X'", 500 → generic). No inline alerts here to avoid duplicating
  // the toast surface (Copilot review 18:48Z, comments 3 / 4 / 5 / 6 / 8).
  const handleAssign = () => {
    if (!selectedRoleId) return;
    assignMutation.mutate(
      { userId, roleId: selectedRoleId },
      { onSuccess: () => setSelectedRoleId('') },
    );
  };


  if (rolesQuery.isLoading) {
    return <p>Loading roles...</p>;
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h3 className="font-semibold mb-2">Assigned Roles</h3>
        {currentRoles.length === 0 && (
          <p className="text-mp-muted text-sm">No roles assigned.</p>
        )}
        <ul className="space-y-2">
          {currentRoles.map((role) => {
            // Same defensive default as selectedRole above — missing flag = treat as elevated.
            const roleIsElevated = role.is_elevated ?? true;
            return (
              <li key={role.id} className="flex items-center justify-between gap-2 p-2 border border-mp-border rounded">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{roleLabel(role)}</span>
                  {roleIsElevated && (
                    <Badge variant="warning">Elevated</Badge>
                  )}
                  {role.description && (
                    <span className="text-mp-muted text-sm">{role.description}</span>
                  )}
                </div>
                <PermissionGuard
                  permission={{
                    resource: 'user',
                    action: roleIsElevated ? 'assign_admin_role' : 'assign_role',
                  }}
                >
                  <Button
                    size="xs"
                    variant="danger"
                    onClick={() => {
                      if (!window.confirm(`Remove role '${roleLabel(role)}' from this user?`)) return;
                      removeMutation.mutate({ userId, roleId: role.id });
                    }}
                    disabled={removeMutation.isPending}
                  >
                    Remove
                  </Button>
                </PermissionGuard>
              </li>
            );
          })}
        </ul>
      </div>

      <div>
        <h3 className="font-semibold mb-2">Assign Role</h3>
        {availableRolesQuery.isError && (
          <Alert variant="error">Failed to load available roles.</Alert>
        )}
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Select
              value={selectedRoleId}
              disabled={availableRolesQuery.isLoading}
              onChange={setSelectedRoleId}
              placeholder="Select a role..."
              options={availableRoles.map((role) => ({
                value: role.id,
                // Same defensive default as roleIsElevated/isElevated above — missing flag = treat as elevated.
                label: (role.is_elevated ?? true)
                  ? `${roleLabel(role)} (Elevated)`
                  : roleLabel(role),
              }))}
            />
          </div>
          {availableRolesQuery.isLoading && <LoadingSpinner size="sm" decorative className="ml-2" />}
          {selectedRoleId && isElevated ? (
            <PermissionGuard permission={{ resource: 'user', action: 'assign_admin_role' }}>
              <Button
                size="sm"
                onClick={handleAssign}
                disabled={!selectedRoleId || assignMutation.isPending}
                variant="warning"
                startIcon={<CircleAlertIcon size={16} />}
              >
                Assign Elevated Role
              </Button>
            </PermissionGuard>
          ) : (
            <PermissionGuard permission={{ resource: 'user', action: 'assign_role' }}>
              <Button
                size="sm"
                onClick={handleAssign}
                disabled={!selectedRoleId || assignMutation.isPending}
              >
                Assign Role
              </Button>
            </PermissionGuard>
          )}
        </div>
      </div>
    </div>
  );
}
